<script setup>
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useTaskNavigation } from 'dashboard/composables/useTaskNavigation';
import { useTaskDueDate } from 'dashboard/composables/useTaskDueDate';
import { channelDotClass } from '../taskChannelDot';

const props = defineProps({
  task: { type: Object, required: true },
});

const emit = defineEmits(['edit']);

const { t } = useI18n();
const { openTask } = useTaskNavigation();
const { label: dueLabel, isOverdue } = useTaskDueDate(toRef(props, 'task'));

const contactName = computed(
  () => props.task.contact?.name || t('TASKS_DASHBOARD.UNNAMED_CONTACT')
);

const channelDot = computed(() =>
  channelDotClass(props.task.conversation?.channel)
);

// An unassigned task still belongs to whoever created it — see
// TaskTable.vue for the same fallback with a visible label; here there's
// only room for the avatar, so the implicit case gets a title tooltip.
const owner = computed(() => props.task.assignee || props.task.createdBy);
const isImplicitOwner = computed(
  () => !props.task.assignee && !!props.task.createdBy
);
const ownerTitle = computed(() =>
  isImplicitOwner.value
    ? `${owner.value.name} · ${t('TASKS_DASHBOARD.CREATED_BY_SUFFIX')}`
    : owner.value?.name
);

const handleEdit = () => emit('edit', props.task);
const handleOpen = event => {
  event.stopPropagation();
  openTask(props.task);
};
</script>

<template>
  <div
    class="flex flex-col gap-2 px-3 py-2.5 rounded-xl bg-n-solid-2 border border-n-container shadow-sm cursor-grab hover:border-n-teal-7 hover:shadow-md transition-[border-color,box-shadow] group/kanban-card"
    @click="handleEdit"
  >
    <div class="flex items-start justify-between gap-2">
      <span
        class="text-sm font-medium leading-snug text-n-slate-12 line-clamp-3 whitespace-pre-line"
      >
        {{ task.description }}
      </span>
      <Button
        icon="i-lucide-external-link"
        size="xs"
        variant="ghost"
        color="slate"
        class="opacity-0 group-hover/kanban-card:opacity-100 flex-shrink-0 -mr-1 -mt-1"
        @click="handleOpen"
      />
    </div>
    <div class="flex items-center gap-1.5 min-w-0">
      <span class="size-1.5 rounded-full flex-shrink-0" :class="channelDot" />
      <span class="text-xs text-n-slate-11 truncate">{{ contactName }}</span>
    </div>
    <div class="flex items-center justify-between gap-2">
      <span
        v-if="dueLabel"
        class="inline-flex items-center text-xs rounded-full px-2 py-0.5 font-medium truncate"
        :class="
          isOverdue
            ? 'bg-n-ruby-9/10 text-n-ruby-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        {{ dueLabel }}
      </span>
      <span v-else />
      <span v-if="owner" :title="ownerTitle" class="flex-shrink-0">
        <Avatar
          :name="owner.name"
          :src="owner.thumbnail"
          :size="20"
          rounded-full
        />
      </span>
      <span
        v-else
        class="size-5 rounded-full bg-n-alpha-2 text-n-slate-10 text-[10px] font-semibold flex items-center justify-center flex-shrink-0"
        :title="t('TASKS_DASHBOARD.NO_ASSIGNEE')"
      >
        ?
      </span>
    </div>
  </div>
</template>
